import React from 'react';
import styles from '../styles/RealtimeComparisonView.module.css';

/**
 * Real-Time Comparison View Component
 * Displays cohort-based benchmark comparison for new users (Days 0-13)
 *
 * Shows user's percentile rankings compared to others at the same journey stage
 *
 * Props:
 * - cohortComparison: Object containing metrics with percentile data
 * - stageFriendlyName: User-friendly name of the journey stage (e.g., "users in their first 3 days")
 * - daysActive: Number of days user has been active
 */
function RealtimeComparisonView({ cohortComparison, stageFriendlyName, daysActive }) {
  if (!cohortComparison) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>Comparison data not available yet.</p>
          <p className={styles.helpText}>Keep interacting to build your progress profile!</p>
        </div>
      </div>
    );
  }

  // Define which metrics to display (priority order)
  const metricsToDisplay = [
    { key: 'todos', label: 'Task Completion', icon: '✓' },
    { key: 'learning', label: 'Learning Progress', icon: '📚' },
    { key: 'conversation', label: 'Engagement Level', icon: '💬' },
    { key: 'barriers', label: 'Self-Awareness', icon: '💡', specialMessage: true }
  ];

  // Filter to only show metrics with data
  const availableMetrics = metricsToDisplay.filter(
    m => cohortComparison[m.key] && cohortComparison[m.key].cohortSize > 0
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Your Progress Among Peers</h2>
        <p className={styles.subtitle}>
          Compared to <span className={styles.cohortName}>{stageFriendlyName}</span>
        </p>
        <p className={styles.daysActive}>Day {daysActive} of your journey</p>
      </div>

      {/* Metrics */}
      <div className={styles.metricsGrid}>
        {availableMetrics.map((metric) => {
          const data = cohortComparison[metric.key];
          const rank = data.rank || 'building';
          const percentile = data.percentile || 0;

          return (
            <div key={metric.key} className={styles.metricCard}>
              {/* Icon and Label */}
              <div className={styles.metricHeader}>
                <span className={styles.metricIcon}>{metric.icon}</span>
                <span className={styles.metricLabel}>{metric.label}</span>
              </div>

              {/* Percentile Bar */}
              <div className={styles.percentileContainer}>
                <div className={styles.percentileBar}>
                  <div
                    className={`${styles.percentileFill} ${styles[rank]}`}
                    style={{ width: `${percentile}%` }}
                  >
                    <span className={styles.percentileValue}>{percentile}%</span>
                  </div>
                </div>
              </div>

              {/* Message */}
              <p className={styles.metricMessage}>{data.message}</p>

              {/* Cohort Size (subtle) */}
              {data.cohortSize > 5 && (
                <p className={styles.cohortSize}>
                  vs. {data.cohortSize} {data.cohortSize === 1 ? 'user' : 'users'}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer message if user is doing well */}
      {availableMetrics.some(m => cohortComparison[m.key]?.percentile >= 70) && (
        <div className={styles.footer}>
          <p className={styles.footerMessage}>
            🎉 You're making great progress! Keep going!
          </p>
        </div>
      )}

      {/* Info note */}
      <div className={styles.infoNote}>
        <p className={styles.infoText}>
          💡 Comparisons update in real-time as you progress through your journey.
        </p>
      </div>
    </div>
  );
}

export default RealtimeComparisonView;
